/**
 * Notification Settings Helpers
 *
 * Normalize notification settings rows and map notification events to the
 * preference buckets used by the UI and services.
 *
 * Primary responsibilities:
 * - Map `notification_settings` rows into exact app types
 * - Build insert/update payloads for notification settings writes
 * - Map notification event types to their controlling preference keys
 *
 * Dependencies:
 * - Supabase generated types
 * - Notification type definitions
 */

import type {
  Tables,
  TablesInsert,
  TablesUpdate
} from '@/supabase/types'
import {
  DEFAULT_NOTIFICATION_SETTINGS,
  type Notification,
  type NotificationChannel,
  type NotificationPreference,
  type NotificationPriority,
  type NotificationSettings,
  type NotificationSettingsKey,
  type NotificationType
} from '@/types/notifications'

type NotificationSettingsRow = Tables<'notification_settings'>
type NotificationSettingsInput = Partial<NotificationSettingsRow> &
  Pick<NotificationSettingsRow, 'user_id'>

type PersistedNotificationSettings = Omit<
  NotificationSettings,
  'id' | 'userId' | 'createdAt' | 'updatedAt'
>

type NotificationSettingsValue =
  | NotificationPreference
  | NotificationSettings['quietHours']
  | NotificationSettings['emailDigest']
  | NotificationSettings['desktopNotifications']

const NOTIFICATION_CHANNELS: NotificationChannel[] = [
  'desktop',
  'email',
  'in_app'
]

const NOTIFICATION_PRIORITIES: NotificationPriority[] = [
  'low',
  'medium',
  'high',
  'urgent'
]

export const NOTIFICATION_SETTINGS_FIELDS = {
  taskDueSoon: 'task_due_soon',
  taskOverdue: 'task_overdue',
  taskStarting: 'task_starting',
  taskAssigned: 'task_assigned',
  taskCompleted: 'task_completed',
  workspaceInvitation: 'workspace_invitation',
  workspaceMemberAdded: 'workspace_member_added',
  workspaceMemberRemoved: 'workspace_member_removed',
  workspaceMemberRoleChanged: 'workspace_member_role_changed',
  spaceShared: 'space_shared',
  commentAdded: 'comment_added',
  mention: 'mention',
  agentFailure: 'agent_failure',
  agentRecovered: 'agent_recovered',
  integrationFailure: 'integration_failure',
  integrationRecovered: 'integration_recovered',
  performanceAlert: 'performance_alert',
  systemAlert: 'system_alert',
  sidecarCompletion: 'sidecar_completion',
  dailyReport: 'daily_report',
  quietHours: 'quiet_hours',
  emailDigest: 'email_digest',
  desktopNotifications: 'desktop_notifications'
} as const

export const NOTIFICATION_TYPE_TO_SETTINGS_KEY: Record<
  NotificationType,
  NotificationSettingsKey
> = {
  task_due_soon: 'taskDueSoon',
  task_overdue: 'taskOverdue',
  task_starting: 'taskStarting',
  task_assigned: 'taskAssigned',
  task_completed: 'taskCompleted',
  workspace_invitation: 'workspaceInvitation',
  workspace_member_added: 'workspaceMemberAdded',
  workspace_member_removed: 'workspaceMemberRemoved',
  workspace_member_role_changed: 'workspaceMemberRoleChanged',
  workspace_badge_earned: 'spaceShared',
  workspace_goal_reached: 'spaceShared',
  workspace_kudos_received: 'spaceShared',
  workspace_weekly_team_summary: 'dailyReport',
  workspace_llm_access_request: 'systemAlert',
  space_invitation: 'spaceShared',
  space_invitation_cancelled: 'spaceShared',
  space_invitation_declined: 'spaceShared',
  space_joined: 'spaceShared',
  space_member_added: 'spaceShared',
  space_member_removed: 'spaceShared',
  space_member_role_changed: 'spaceShared',
  space_shared: 'spaceShared',
  comment_added: 'commentAdded',
  mention: 'mention',
  reminder: 'taskDueSoon',
  agent_failure: 'agentFailure',
  agent_recovered: 'agentRecovered',
  integration_failure: 'integrationFailure',
  integration_recovered: 'integrationRecovered',
  performance_alert: 'performanceAlert',
  system_alert: 'systemAlert',
  sidecar_completion: 'sidecarCompletion',
  daily_report: 'dailyReport'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function sanitizeChannels(
  value: unknown,
  fallback: NotificationChannel[]
): NotificationChannel[] {
  if (!Array.isArray(value)) {
    return fallback
  }

  const channels = value.filter(
    (item): item is NotificationChannel =>
      typeof item === 'string' &&
      NOTIFICATION_CHANNELS.includes(item as NotificationChannel)
  )

  return channels.length > 0 ? channels : fallback
}

function sanitizePriority(
  value: unknown,
  fallback: NotificationPriority
): NotificationPriority {
  if (
    typeof value === 'string' &&
    NOTIFICATION_PRIORITIES.includes(value as NotificationPriority)
  ) {
    return value as NotificationPriority
  }

  return fallback
}

function normalizeNotificationPreference(
  value: unknown,
  fallback: NotificationPreference
): NotificationPreference {
  if (!isRecord(value)) {
    return { ...fallback }
  }

  return {
    enabled:
      typeof value.enabled === 'boolean' ? value.enabled : fallback.enabled,
    channels: sanitizeChannels(value.channels, fallback.channels),
    priority: sanitizePriority(value.priority, fallback.priority),
    ...(typeof value.advanceTime === 'number'
      ? { advanceTime: value.advanceTime }
      : fallback.advanceTime !== undefined
        ? { advanceTime: fallback.advanceTime }
        : {})
  }
}

function normalizeQuietHours(
  value: unknown,
  fallback: NotificationSettings['quietHours']
): NotificationSettings['quietHours'] {
  if (!isRecord(value)) {
    return { ...fallback }
  }

  return {
    enabled:
      typeof value.enabled === 'boolean' ? value.enabled : fallback.enabled,
    startTime:
      typeof value.startTime === 'string'
        ? value.startTime
        : fallback.startTime,
    endTime:
      typeof value.endTime === 'string' ? value.endTime : fallback.endTime,
    timezone:
      typeof value.timezone === 'string' ? value.timezone : fallback.timezone
  }
}

function normalizeEmailDigest(
  value: unknown,
  fallback: NotificationSettings['emailDigest']
): NotificationSettings['emailDigest'] {
  if (!isRecord(value)) {
    return { ...fallback }
  }

  const frequency =
    value.frequency === 'daily' ||
    value.frequency === 'weekly' ||
    value.frequency === 'never'
      ? value.frequency
      : fallback.frequency

  return {
    enabled:
      typeof value.enabled === 'boolean' ? value.enabled : fallback.enabled,
    frequency,
    time: typeof value.time === 'string' ? value.time : fallback.time
  }
}

function normalizeDesktopNotifications(
  value: unknown,
  fallback: NotificationSettings['desktopNotifications']
): NotificationSettings['desktopNotifications'] {
  if (!isRecord(value)) {
    return { ...fallback }
  }

  return {
    enabled:
      typeof value.enabled === 'boolean' ? value.enabled : fallback.enabled,
    showPreview:
      typeof value.showPreview === 'boolean'
        ? value.showPreview
        : fallback.showPreview,
    sound:
      typeof value.sound === 'boolean' ? value.sound : fallback.sound
  }
}

function getPersistedSettings(
  settings: Partial<NotificationSettings> | PersistedNotificationSettings
): PersistedNotificationSettings {
  return {
    taskDueSoon: settings.taskDueSoon ?? DEFAULT_NOTIFICATION_SETTINGS.taskDueSoon,
    taskOverdue: settings.taskOverdue ?? DEFAULT_NOTIFICATION_SETTINGS.taskOverdue,
    taskAssigned: settings.taskAssigned ?? DEFAULT_NOTIFICATION_SETTINGS.taskAssigned,
    taskCompleted: settings.taskCompleted ?? DEFAULT_NOTIFICATION_SETTINGS.taskCompleted,
    workspaceInvitation:
      settings.workspaceInvitation ?? DEFAULT_NOTIFICATION_SETTINGS.workspaceInvitation,
    workspaceMemberAdded:
      settings.workspaceMemberAdded ??
      DEFAULT_NOTIFICATION_SETTINGS.workspaceMemberAdded,
    workspaceMemberRemoved:
      settings.workspaceMemberRemoved ??
      DEFAULT_NOTIFICATION_SETTINGS.workspaceMemberRemoved,
    workspaceMemberRoleChanged:
      settings.workspaceMemberRoleChanged ??
      DEFAULT_NOTIFICATION_SETTINGS.workspaceMemberRoleChanged,
    spaceShared: settings.spaceShared ?? DEFAULT_NOTIFICATION_SETTINGS.spaceShared,
    commentAdded: settings.commentAdded ?? DEFAULT_NOTIFICATION_SETTINGS.commentAdded,
    mention: settings.mention ?? DEFAULT_NOTIFICATION_SETTINGS.mention,
    agentFailure: settings.agentFailure ?? DEFAULT_NOTIFICATION_SETTINGS.agentFailure,
    agentRecovered:
      settings.agentRecovered ?? DEFAULT_NOTIFICATION_SETTINGS.agentRecovered,
    integrationFailure:
      settings.integrationFailure ??
      DEFAULT_NOTIFICATION_SETTINGS.integrationFailure,
    integrationRecovered:
      settings.integrationRecovered ??
      DEFAULT_NOTIFICATION_SETTINGS.integrationRecovered,
    performanceAlert:
      settings.performanceAlert ?? DEFAULT_NOTIFICATION_SETTINGS.performanceAlert,
    systemAlert: settings.systemAlert ?? DEFAULT_NOTIFICATION_SETTINGS.systemAlert,
    sidecarCompletion:
      settings.sidecarCompletion ??
      DEFAULT_NOTIFICATION_SETTINGS.sidecarCompletion,
    dailyReport: settings.dailyReport ?? DEFAULT_NOTIFICATION_SETTINGS.dailyReport,
    quietHours: settings.quietHours ?? DEFAULT_NOTIFICATION_SETTINGS.quietHours,
    emailDigest: settings.emailDigest ?? DEFAULT_NOTIFICATION_SETTINGS.emailDigest,
    desktopNotifications:
      settings.desktopNotifications ??
      DEFAULT_NOTIFICATION_SETTINGS.desktopNotifications
  }
}

export function mapNotificationSettingsRow(
  row: NotificationSettingsInput
): NotificationSettings {
  const fallbackTimestamp = new Date()

  return {
    id: row.id ?? `default:${row.user_id}`,
    userId: row.user_id,
    taskDueSoon: normalizeNotificationPreference(
      row.task_due_soon,
      DEFAULT_NOTIFICATION_SETTINGS.taskDueSoon
    ),
    taskOverdue: normalizeNotificationPreference(
      row.task_overdue,
      DEFAULT_NOTIFICATION_SETTINGS.taskOverdue
    ),
    taskAssigned: normalizeNotificationPreference(
      row.task_assigned,
      DEFAULT_NOTIFICATION_SETTINGS.taskAssigned
    ),
    taskCompleted: normalizeNotificationPreference(
      row.task_completed,
      DEFAULT_NOTIFICATION_SETTINGS.taskCompleted
    ),
    workspaceInvitation: normalizeNotificationPreference(
      row.workspace_invitation,
      DEFAULT_NOTIFICATION_SETTINGS.workspaceInvitation
    ),
    workspaceMemberAdded: normalizeNotificationPreference(
      row.workspace_member_added,
      DEFAULT_NOTIFICATION_SETTINGS.workspaceMemberAdded
    ),
    workspaceMemberRemoved: normalizeNotificationPreference(
      row.workspace_member_removed,
      DEFAULT_NOTIFICATION_SETTINGS.workspaceMemberRemoved
    ),
    workspaceMemberRoleChanged: normalizeNotificationPreference(
      row.workspace_member_role_changed,
      DEFAULT_NOTIFICATION_SETTINGS.workspaceMemberRoleChanged
    ),
    spaceShared: normalizeNotificationPreference(
      row.space_shared,
      DEFAULT_NOTIFICATION_SETTINGS.spaceShared
    ),
    commentAdded: normalizeNotificationPreference(
      row.comment_added,
      DEFAULT_NOTIFICATION_SETTINGS.commentAdded
    ),
    mention: normalizeNotificationPreference(
      row.mention,
      DEFAULT_NOTIFICATION_SETTINGS.mention
    ),
    agentFailure: normalizeNotificationPreference(
      row.agent_failure,
      DEFAULT_NOTIFICATION_SETTINGS.agentFailure
    ),
    agentRecovered: normalizeNotificationPreference(
      row.agent_recovered,
      DEFAULT_NOTIFICATION_SETTINGS.agentRecovered
    ),
    integrationFailure: normalizeNotificationPreference(
      row.integration_failure,
      DEFAULT_NOTIFICATION_SETTINGS.integrationFailure
    ),
    integrationRecovered: normalizeNotificationPreference(
      row.integration_recovered,
      DEFAULT_NOTIFICATION_SETTINGS.integrationRecovered
    ),
    performanceAlert: normalizeNotificationPreference(
      row.performance_alert,
      DEFAULT_NOTIFICATION_SETTINGS.performanceAlert
    ),
    systemAlert: normalizeNotificationPreference(
      row.system_alert,
      DEFAULT_NOTIFICATION_SETTINGS.systemAlert
    ),
    sidecarCompletion: normalizeNotificationPreference(
      row.sidecar_completion,
      DEFAULT_NOTIFICATION_SETTINGS.sidecarCompletion
    ),
    dailyReport: normalizeNotificationPreference(
      row.daily_report,
      DEFAULT_NOTIFICATION_SETTINGS.dailyReport
    ),
    quietHours: normalizeQuietHours(
      row.quiet_hours,
      DEFAULT_NOTIFICATION_SETTINGS.quietHours
    ),
    emailDigest: normalizeEmailDigest(
      row.email_digest,
      DEFAULT_NOTIFICATION_SETTINGS.emailDigest
    ),
    desktopNotifications: normalizeDesktopNotifications(
      row.desktop_notifications,
      DEFAULT_NOTIFICATION_SETTINGS.desktopNotifications
    ),
    createdAt: row.created_at ? new Date(row.created_at) : fallbackTimestamp,
    updatedAt: row.updated_at ? new Date(row.updated_at) : fallbackTimestamp
  }
}

export function buildDefaultNotificationSettings(
  userId: string
): NotificationSettings {
  return mapNotificationSettingsRow({ user_id: userId })
}

export function buildNotificationSettingsInsert(
  userId: string,
  settings: Partial<NotificationSettings> | PersistedNotificationSettings = DEFAULT_NOTIFICATION_SETTINGS
): TablesInsert<'notification_settings'> {
  const persistedSettings = getPersistedSettings(settings)

  return {
    user_id: userId,
    task_due_soon: persistedSettings.taskDueSoon,
    task_overdue: persistedSettings.taskOverdue,
    task_assigned: persistedSettings.taskAssigned,
    task_completed: persistedSettings.taskCompleted,
    workspace_invitation: persistedSettings.workspaceInvitation,
    workspace_member_added: persistedSettings.workspaceMemberAdded,
    workspace_member_removed: persistedSettings.workspaceMemberRemoved,
    workspace_member_role_changed: persistedSettings.workspaceMemberRoleChanged,
    space_shared: persistedSettings.spaceShared,
    comment_added: persistedSettings.commentAdded,
    mention: persistedSettings.mention,
    agent_failure: persistedSettings.agentFailure,
    agent_recovered: persistedSettings.agentRecovered,
    integration_failure: persistedSettings.integrationFailure,
    integration_recovered: persistedSettings.integrationRecovered,
    performance_alert: persistedSettings.performanceAlert,
    system_alert: persistedSettings.systemAlert,
    sidecar_completion: persistedSettings.sidecarCompletion,
    daily_report: persistedSettings.dailyReport,
    quiet_hours: persistedSettings.quietHours,
    email_digest: persistedSettings.emailDigest,
    desktop_notifications: persistedSettings.desktopNotifications
  }
}

export function buildNotificationSettingsUpdate(
  settings: Partial<NotificationSettings>
): TablesUpdate<'notification_settings'> {
  const update: TablesUpdate<'notification_settings'> = {}

  for (const [key, column] of Object.entries(NOTIFICATION_SETTINGS_FIELDS) as Array<
    [keyof typeof NOTIFICATION_SETTINGS_FIELDS, keyof TablesUpdate<'notification_settings'>]
  >) {
    const value = settings[key as keyof NotificationSettings] as
      | NotificationSettingsValue
      | undefined

    if (value !== undefined) {
      update[column] = value
    }
  }

  return update
}

export function mapNotificationRow(row: Tables<'notifications'>): Notification {
  return {
    id: row.id,
    userId: row.user_id,
    type: row.type as NotificationType,
    title: row.title,
    message: row.message,
    data: isRecord(row.data) ? row.data : undefined,
    priority: row.priority as NotificationPriority,
    channels: row.channels.filter(
      (channel): channel is NotificationChannel =>
        NOTIFICATION_CHANNELS.includes(channel as NotificationChannel)
    ),
    status: row.status as Notification['status'],
    scheduledFor: row.scheduled_for ? new Date(row.scheduled_for) : undefined,
    sentAt: row.sent_at ? new Date(row.sent_at) : undefined,
    readAt: row.read_at ? new Date(row.read_at) : undefined,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at)
  }
}

export function getNotificationPreferenceForType(
  settings: NotificationSettings,
  type: NotificationType
) {
  return settings[NOTIFICATION_TYPE_TO_SETTINGS_KEY[type]]
}
